import React, { useEffect, useState } from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Mail, Send, MessageCircle, Instagram } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, EASE } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { L } from "@/lib/localize";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import JsonLd from "@/components/site/JsonLd";
import { graph, organizationNode, contactPageNode } from "@/lib/schema";
import { useSiteInfo } from "@/lib/siteInfo";
import { CONTACT } from "@/lib/contactInfo";

const HERO_IMG = "https://images.unsplash.com/photo-1708502785172-20c967b619e6?q=80&w=1600&auto=format&fit=crop";

export default function Contact() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  useSeo({ title: t("seo.contact.title"), description: t("seo.contact.desc"), path: "/contatti" });
  const siteInfo = useSiteInfo();
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", destination_interest: params.get("tour") || "", budget: "", message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/leads", form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd
        id="jsonld-contact"
        data={graph([
          organizationNode(siteInfo || {}),
          contactPageNode({ title: t("contact.title"), description: t("seo.contact.desc"), lang }),
        ])}
      />

      <section className="pt-32 sm:pt-44 pb-16" data-testid="contact-page">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow mb-3">{t("contact.eyebrow")}</p>
                <h1 className="font-serif font-semibold text-4xl sm:text-6xl leading-[1.0]">{t("contact.title")}</h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-12 space-y-5">
                  <a href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4 card-dark p-5 hover:border-copper/40 transition-colors" data-testid="contact-email-row">
                    <span className="h-11 w-11 flex items-center justify-center border border-copper/40 text-copper"><Mail className="h-5 w-5" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Email</p>
                      <p className="text-cream group-hover:text-copperlight transition-colors">{CONTACT.email}</p>
                    </div>
                  </a>
                  <a href={`${CONTACT.whatsapp}?text=${encodeURIComponent(t("contact.waMessage"))}`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 card-dark p-5 hover:border-copper/40 transition-colors" data-testid="contact-whatsapp-row">
                    <span className="h-11 w-11 flex items-center justify-center border border-copper/40 text-copper"><MessageCircle className="h-5 w-5" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand">WhatsApp</p>
                      <p className="text-cream group-hover:text-copperlight transition-colors">{CONTACT.phone}</p>
                    </div>
                  </a>
                  <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 card-dark p-5 hover:border-copper/40 transition-colors" data-testid="contact-instagram-row">
                    <span className="h-11 w-11 flex items-center justify-center border border-copper/40 text-copper"><Instagram className="h-5 w-5" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Instagram</p>
                      <p className="text-cream group-hover:text-copperlight transition-colors">{CONTACT.instagramHandle}</p>
                    </div>
                  </a>
                </div>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="mt-12 relative border border-copper/20 overflow-hidden hidden lg:block">
                  <img
                    src={imgSrc(HERO_IMG, 1200)}
                    srcSet={imgSet(HERO_IMG, [400, 800, 1200])}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    alt="Foresta amazzonica"
                    className="w-full aspect-[16/9] object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-obsidian/30" />
                  <div className="absolute inset-0 grain pointer-events-none" />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                {sent ? (
                  <motion.div
                    className="card-dark p-10 text-center"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    data-testid="contact-success"
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-copper text-copper mb-6"><Check className="h-7 w-7" /></span>
                    <h2 className="font-serif text-3xl text-cream">{t("contact.successTitle")}</h2>
                    <p className="mt-3 text-cream2">{t("contact.successText")}</p>
                    <button
                      className="btn-ghost mt-8"
                      data-testid="contact-again-btn"
                      onClick={() => {
                        setSent(false);
                        setForm({ name: "", email: "", phone: "", destination_interest: "", budget: "", message: "" });
                      }}
                    >
                      {t("contact.again")}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="card-dark p-6 sm:p-10 space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="label" htmlFor="c-name">{t("contact.name")}</label>
                        <input id="c-name" required className="field" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                      </div>
                      <div>
                        <label className="label" htmlFor="c-email">{t("contact.email")}</label>
                        <input id="c-email" type="email" required className="field" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="label" htmlFor="c-phone">{t("contact.phone")}</label>
                        <input id="c-phone" className="field" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                      </div>
                      <div>
                        <label className="label" htmlFor="c-dest">{t("contact.destination")}</label>
                        <select id="c-dest" className="field" value={form.destination_interest} onChange={set("destination_interest")} data-testid="contact-destination-select">
                          <option value="">{t("contact.destOther")}</option>
                          {tours.map((tour) => (
                            <option key={tour.id} value={tour.slug}>{L(tour, lang).title}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label" htmlFor="c-budget">{t("contact.budget")}</label>
                      <select id="c-budget" className="field" value={form.budget} onChange={set("budget")} data-testid="contact-budget-select">
                        <option value="">{t("budget.any")}</option>
                        <option value="fino-2000">{t("budget.b1")}</option>
                        <option value="2000-3000">{t("budget.b2")}</option>
                        <option value="3000-4000">{t("budget.b3")}</option>
                        <option value="4000+">{t("budget.b4")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="label" htmlFor="c-msg">{t("contact.message")}</label>
                      <textarea id="c-msg" rows="5" className="field resize-none" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                    </div>
                    <label className="flex items-start gap-3 text-sm text-cream2 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={form.consent}
                        onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                        className="accent-[#C86D3B] w-4 h-4 mt-0.5"
                        data-testid="contact-consent"
                      />
                      <span>
                        {t("consent.pre")}{" "}
                        <Link to="/privacy" className="link-underline text-copperlight" data-testid="contact-privacy-link">{t("consent.link")}</Link> *
                      </span>
                    </label>
                    {error && <p className="text-sm text-red-400" data-testid="contact-error">{error}</p>}
                    <button type="submit" disabled={sending} className="btn-copper w-full sm:w-auto disabled:opacity-50" data-testid="contact-submit-btn">
                      <Send className="h-4 w-4" /> {sending ? "…" : t("contact.submit")}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
